/* ------------------------------------------------------------------
 * SupplierValidation.js
 * Validation rules + helpers for SupplierPageMaster.
 * Limits are based on the live supplier page layout (single-line pills,
 * 2-line card titles, 3-line card descriptions, 6-step timeline, etc).
 * ------------------------------------------------------------------ */

/* ---------- Text rules (keyed "group.key") ---------- */
export const TEXT_RULES = {
  // Hero
  "hero.badge": { required: true, max: 40, help: "Small pill above the heading." },
  "hero.heading": { required: true, max: 60, help: "Shown in 2 lines on desktop." },
  "hero.subheading": { required: true, max: 200, help: "Long text is clamped on the page." },
  "hero.primary_cta_label": { required: true, max: 30 },
  "hero.secondary_cta_label": { max: 30 },

  // Benefits
  "benefits.heading": { required: true, max: 50 },

  // Features
  "features.heading": { required: true, max: 60 },
  "features.cta_label": { max: 30 },

  // How it works
  "working.heading": { required: true, max: 40 },
  "working.subheading": { max: 100 },

  // Industries
  "industries.heading": { required: true, max: 60 },

  // Comparison
  "comparison.heading": { required: true, max: 60 },
  "comparison.subheading": { max: 100 },
  "comparison.left_title": { required: true, max: 30 },
  "comparison.right_title": { required: true, max: 30 },
  "comparison.item": { required: true, max: 45 },

  // Case studies / testimonials
  "case_studies.heading": { max: 60 },
  "case_studies.subheading": { max: 150 },
  "testimonials.heading": { max: 60 },
  "testimonials.subheading": { max: 150 },

  // CTA
  "cta.heading": { required: true, max: 60 },
  "cta.subheading": { max: 160 },
  "cta.primary_cta_label": { required: true, max: 30 },
  "cta.secondary_cta_label": { max: 30 },
  "cta.benefit.text": {required: true,min: 10,max: 30},
};

/* ---------- Rules for fields inside repeater items ---------- */
export const ITEM_RULES = {
  metric: {
    number: { required: true, max: 10 },
    label: { required: true, max: 30 },
  },
  benefit: {
    title: { required: true, max: 20 },
    description: { required: true, max: 80 },
  },
  feature: {
    title: { required: true, max: 35 },
    description: { required: true, max: 90 },
  },
  step: {
    title: { required: true, max: 25 },
    description: { required: true, max: 100 },
  },
  faq: {
    question: { required: true, max: 100 },
    answer: { required: true, max: 400 },
  },
  
};

/* ---------- Repeater count rules ---------- */
export const REPEATER_RULES = {
  "metrics.items": { min: 4, max: 6, noun: "metric", help: "Shown as one row of metrics." },
  "benefits.items": { min: 3, max: 6, noun: "benefit" },
  "features.items": { min: 3, max: 5, noun: "feature", help: "Page shows 5 feature cards in a row." },
  "working.steps": { min: 6, max: 9, noun: "step", help: "Page shows a 6-step timeline." },
  "comparison.left_items": { min: 3, max: 6, noun: "point" },
  "comparison.right_items": { min: 3, max: 6, noun: "point" },
  faqs: { min: 1, max: 12, noun: "FAQ" },
  "cta.benefits": { min: 2, max: 3, noun: "benefit" },
};

/* ---------- Image specs ---------- */
const FORMATS = ["image/png", "image/jpeg", "image/webp"];

export const IMAGE_SPECS = {
  hero_desktop_image: {
    formats: FORMATS,
    maxSizeMB: 2,
    minWidth: 1920,
    minHeight: 1080,
    help:"JPG, PNG, WebP · Max 2 MB · 1920×1080 px minimum · 16:9 ratio. Important content should stay in the right 40% of the image. Keep the left side clean for headings and CTA buttons. Avoid text, logos, buttons, dashboards, or UI elements inside  image.",

  },
  hero_mobile_image: {
    formats: FORMATS,
    maxSizeMB: 2,
    minWidth: 1080,
    minHeight: 1920,
    help: "PNG, JPG or WebP · max 2 MB · min 1080×1920 px.9:16 ratio.Keep the center area clean for content overlay. Avoid text, logos, buttons, dashboards, or UI elements inside the image.",
  },
  og_image: {
    formats: FORMATS,
    maxSizeMB: 2,
    // minWidth: 1200,
    // minHeight: 630,
    help: "PNG, JPG or WebP · max 2 MB · min 1200×630 px.",
  },
  twitter_image: {
    formats: FORMATS,
    maxSizeMB: 2,
    // minWidth: 1200,
    // minHeight: 630,
    help: "PNG, JPG or WebP · max 2 MB · min 1200×630 px.",
  },
};

/* ---------- Validators ---------- */
const toStr = (value) => (value === null || value === undefined ? "" : String(value)).trim();

export const validateText = (value, rules, label = "This field") => {
  if (!rules) return null;
  const text = toStr(value);
  if (!text) return rules.required ? `${label} is required.` : null;
  if (rules.min && text.length < rules.min) return `${label} must be at least ${rules.min} characters.`;
  if (rules.max && text.length > rules.max) return `${label} must be at most ${rules.max} characters.`;
  return null;
};

// Same behaviour, used for repeater item fields
export const validateByRules = (value, rules, label) => validateText(value, rules, label);

// Accepts relative paths ("/login") and http(s) URLs
export const validateUrl = (value, required = false, label = "URL") => {
  const url = toStr(value);
  if (!url) return required ? `${label} is required.` : null;
  if (/\s/.test(url)) return `${label} must not contain spaces.`;
  if (url.startsWith("/")) return null;
  try {
    const parsed = new URL(url);
    if (!["http:", "https:"].includes(parsed.protocol)) return `${label} must start with http:// or https://, or be a path like /login.`;
    return null;
  } catch {
    return `${label} must be a valid URL or a path like /login.`;
  }
};

export const validateRepeaterCount = (items = [], rules) => {
  if (!rules) return null;
  const count = items.length;
  const noun = rules.noun || "item";
  if (rules.min && count < rules.min) return `Add at least ${rules.min} ${noun}${rules.min > 1 ? "s" : ""}.`;
  if (rules.max && count > rules.max) return `You can add at most ${rules.max} ${noun}${rules.max > 1 ? "s" : ""}.`;
  return null;
};

const readImageSize = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

// Returns an array of problems (empty array = OK)
export const validateImageBeforeUpload = async (file, fieldName) => {
  const spec = IMAGE_SPECS[fieldName];
  const problems = [];
  if (!file || !spec) return problems;

  if (!spec.formats.includes(file.type)) {
    problems.push("Only PNG, JPG or WebP images are allowed.");
  }
  if (spec.maxSizeMB && file.size > spec.maxSizeMB * 1024 * 1024) {
    problems.push(`Image must be under ${spec.maxSizeMB} MB.`);
  }
  if (spec.minWidth || spec.minHeight) {
    const size = await readImageSize(file);
    if (!size) {
      problems.push("Could not read this image.");
    } else {
      if (spec.minWidth && size.width < spec.minWidth) problems.push(`Width must be at least ${spec.minWidth}px (yours is ${size.width}px).`);
      if (spec.minHeight && size.height < spec.minHeight) problems.push(`Height must be at least ${spec.minHeight}px (yours is ${size.height}px).`);
    }
  }
  const size = await readImageSize(file);
  if (spec.aspectRatio) {
  const actualRatio = size.width / size.height;
  const tolerance = spec.aspectRatioTolerance || 0.05;

  if (Math.abs(actualRatio - spec.aspectRatio) > tolerance) {
    problems.push(
      `Image must use a ${spec.aspectRatio > 1 ? "16:9" : "9:16"} aspect ratio.`
    );
  }
}
  return problems;
};